/**
 * @file order-actions.ts
 * @description Реестр переходов заказа: какие действия доступны в статусе и
 * как они называются (phase19.24 этап 1, шаг 5.3).
 *
 * Переехал из `switch` в `OrderDetailPage`: список действий нужен теперь в
 * двух местах — меню «⋯» шапки и подбор главной кнопки, — и две копии
 * `switch` разъехались бы на первом же новом статусе.
 *
 * Правило phase19.14 действует: кнопки, которая гарантированно вернёт 409, в
 * реестре нет (ручное «Упаковано» на сборке убрано — стадию `PACKED` ставит
 * только проведение задания).
 *
 * Действия карточки бывают трёх видов, и все три ходят через один ключ:
 * вызов эндпоинта заказа (`confirm`), перевод в стадию по её виду
 * (`handOver` → `DELIVERED` через `POST /transition`) и действие рядом с
 * заказом (`requestReview` — письмо клиенту). Один ключ — потому что кнопка,
 * меню «⋯» и sticky-полоса обязаны звать одно и то же: вторая сигнатура
 * обработчика неизбежно разъехалась бы с первой.
 */

import {
  Ban,
  Check,
  CreditCard,
  Flag,
  HandCoins,
  Handshake,
  PackageCheck,
  Send,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { OrderStageKind, OrderStatus, orderAction } from "./api-orders";

/**
 * Переходы, у которых нет своего эндпоинта: ведём заказ в стадию по её виду
 * через `POST /admin/orders/:id/transition`. Реестр, а не `if`: выдача
 * самовывоза — первая такая кнопка, следующая добавится записью.
 */
export const ORDER_STAGE_TRANSITIONS = {
  /** Выдача заказа со склада самовывоза: стадия «Вручён». */
  handOver: "DELIVERED",
  /**
   * Согласование доставки с клиентом (решение пользователя 2026-09-21): если
   * способ доставки не выбран, её стоимость меняет сумму заказа, поэтому
   * согласуют ДО оплаты. Стадия «Согласование доставки».
   */
  agreeDelivery: "PENDING_DELIVERY_AGREEMENT",
} as const satisfies Record<string, OrderStageKind>;

/**
 * Действия рядом с заказом: заказ не двигают, но живут в той же кнопке и в
 * том же меню. Выполняет их страница карточки (диалог), а не переходы.
 */
export const ORDER_SIDE_ACTIONS = ["requestReview", "sendAgreement"] as const;

export type OrderStageTransitionKey = keyof typeof ORDER_STAGE_TRANSITIONS;
export type OrderSideActionKey = (typeof ORDER_SIDE_ACTIONS)[number];

/** Ключ действия карточки: эндпоинт заказа, перевод в стадию или «рядом». */
export type OrderActionKey =
  | keyof typeof orderAction
  | OrderStageTransitionKey
  | OrderSideActionKey;

/** Ключ — перевод в стадию (а не вызов эндпоинта заказа)? */
export function isStageTransitionAction(
  key: OrderActionKey,
): key is OrderStageTransitionKey {
  return key in ORDER_STAGE_TRANSITIONS;
}

/** Ключ — действие рядом с заказом (диалог на странице)? */
export function isSideAction(key: OrderActionKey): key is OrderSideActionKey {
  return (ORDER_SIDE_ACTIONS as readonly string[]).includes(key);
}

export interface OrderActionMeta {
  label: string;
  icon: LucideIcon;
  /** Деструктивное действие: в меню «⋯» идёт последним (правило A6). */
  danger?: boolean;
  /**
   * Действие второго плана: живёт в меню «⋯», но главной кнопкой «по
   * статусу» не становится. Иначе «Согласовать доставку» встала бы главной
   * кнопкой у каждого заказа в ожидании оплаты, хотя там ждут денег.
   */
  secondary?: boolean;
}

/** Подписи и иконки переходов — один источник для кнопки и меню. */
export const ORDER_ACTION_META: Record<OrderActionKey, OrderActionMeta> = {
  confirm: { label: "Подтвердить", icon: Check },
  awaitPayment: { label: "К оплате", icon: CreditCard },
  startAssembling: { label: "Начать сборку", icon: PackageCheck },
  pack: { label: "Упаковано", icon: PackageCheck },
  complete: { label: "Завершить", icon: Flag },
  // Самовывоз: клиент пришёл, заказ отдали — стадия «Вручён».
  handOver: { label: "Выдать заказ", icon: HandCoins },
  agreeDelivery: { label: "Согласовать доставку", icon: Handshake, secondary: true },
  // Письмо клиенту с кнопкой подтверждения (phase19.24 шаг 5.1). Действие
  // рядом с заказом: заказ не двигается, открывается подтверждение отправки в
  // блоке «Согласование доставки» — одно окно на кнопку полосы и кнопку блока.
  sendAgreement: { label: "Отправить на подтверждение", icon: Send },
  requestReview: { label: "Запросить отзыв", icon: Send },
  cancel: { label: "Отменить", icon: Ban, danger: true },
};

/**
 * Доступные переходы по статусу. Запись, а не ветка: новый статус = строка.
 * `RETURN_REQUESTED`/`RETURNED` действий не дают — возврат ведётся в своём
 * пайплайне.
 */
export const ORDER_STATUS_ACTIONS: Record<OrderStatus, OrderActionKey[]> = {
  // «Согласовать доставку» доступно, пока деньги не взяты: стоимость доставки
  // меняет сумму заказа (решение пользователя 2026-09-21).
  NEW: ["confirm", "agreeDelivery", "cancel"],
  CONFIRMED: ["awaitPayment", "agreeDelivery", "cancel"],
  AWAITING_PAYMENT: ["agreeDelivery", "cancel"],
  PAID: ["startAssembling", "cancel"],
  // phase19.14: ручного «Упаковано» нет — стадию ставит проведение сборки.
  ASSEMBLING: ["cancel"],
  PACKED: ["cancel"],
  SHIPPED: [],
  // Запрос отзыва живёт в меню и в «Что дальше» — отдельного блока в карточке
  // больше нет (решение 2026-09-21).
  DELIVERED: ["complete", "requestReview"],
  COMPLETED: ["requestReview"],
  CANCELLED: [],
  RETURN_REQUESTED: [],
  RETURNED: [],
};

/**
 * Действия, которые сервер отвергнет, пока доставка не согласована с клиентом
 * (ручной заказ без подтверждённой заявки — инцидент ревью 5.1: «Подтвердить»
 * отвечало 409). Реестр, а не `if` в трёх компонентах.
 */
const DELIVERY_AGREEMENT_BLOCKED: readonly OrderActionKey[] = ["confirm", "awaitPayment"];

/**
 * Что, кроме статуса, решает доступность действия.
 *
 * `agreementFirst` считает один источник — `needsDeliveryAgreement`
 * (`order-chain-texts.ts`): полоса «Сейчас», главная кнопка и меню «⋯»
 * обязаны отвечать одинаково.
 */
export interface OrderActionScope {
  status: OrderStatus;
  /** Сначала согласование доставки: подтверждение и счёт пока запрещены. */
  agreementFirst?: boolean;
}

/** Доступные действия заказа; неизвестный статус — пустой список, не падение. */
export function availableOrderActions(scope: OrderActionScope): OrderActionKey[] {
  const actions = ORDER_STATUS_ACTIONS[scope.status] ?? [];
  if (!scope.agreementFirst) return actions;
  return actions.filter((key) => !DELIVERY_AGREEMENT_BLOCKED.includes(key));
}

/**
 * Разрешено ли действие в текущем СТАТУСЕ заказа. Последний фильтр перед
 * показом кнопки: шкала маршрута строится по стадиям воронки магазина и может
 * разойтись со статусом (инцидент 2026-09-21 — заказ в «Ожидании оплаты»
 * предлагал «Подтвердить», сервер отвечал 409, кнопка «не работала»).
 *
 * Проверяем только то, что реестр статусов описывает. Действие рядом с
 * заказом (`requestReview`) сервер не отвергает вовсе, а «Выдать заказ»
 * (`handOver`) ни в одном статусе не перечислено: его уместность определяет
 * стадия самовывоза, а не статус. Зато «Согласовать доставку» в реестре
 * есть — и за пределами NEW/CONFIRMED/AWAITING_PAYMENT кнопки не будет.
 */
export function isActionAllowedInStatus(
  scope: OrderActionScope,
  key: OrderActionKey,
): boolean {
  if (isSideAction(key)) return true;
  if (!STATUS_GOVERNED_ACTIONS.has(key)) return true;
  return availableOrderActions(scope).includes(key);
}

/** Действия, доступность которых описана реестром статусов. */
const STATUS_GOVERNED_ACTIONS = new Set<OrderActionKey>(
  Object.values(ORDER_STATUS_ACTIONS).flat(),
);

/**
 * Резервная главная кнопка, когда агрегат цепочки недоступен: берём первый
 * неопасный переход статуса. Карточка обязана оставаться рабочей и без
 * «Пути заказа» — иначе недоступный склад лишал бы менеджера всех действий.
 */
export function fallbackPrimaryActionKey(
  scope: OrderActionScope,
  canManage: boolean,
): OrderActionKey | null {
  if (!canManage) return null;
  const actions = availableOrderActions(scope);
  // Пока доставка не согласована, «Согласовать доставку» перестаёт быть
  // второстепенным: это единственное, чем менеджер двигает такой заказ.
  if (scope.agreementFirst && actions.includes("agreeDelivery")) return "agreeDelivery";
  return (
    actions.find(
      (key) => !ORDER_ACTION_META[key].danger && !ORDER_ACTION_META[key].secondary,
    ) ?? null
  );
}
